import { PLANBASE_CONFIG } from '../config';

/**
 * Shared Orbi AI client helpers.
 *
 * Every Orbi caller was rebuilding the same headers + fetch shape;
 * this centralises the small bits that don't need to live in a page.
 */

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Project-ID': PLANBASE_CONFIG.projectId,
    Authorization: `Bearer ${PLANBASE_CONFIG.apiKey}`,
    'X-User-Token': localStorage.getItem('planbase_token') || '',
  };
}

/**
 * Ask the worker to rewrite a prompt so it's more effective for
 * architectural design generation. Returns the improved prompt.
 *
 * The worker action `refine_prompt` is configured to return one
 * flowing paragraph of plain text with no preamble, quotes, or
 * markdown — so the return value can be dropped straight into the
 * composer's text field.
 */
export async function refineOrbiPrompt(prompt: string): Promise<string> {
  const value = prompt.trim();
  if (!value) throw new Error('Nothing to refine yet');
  if (value.length > 4000) throw new Error('Prompt is too long to refine');

  const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      action: 'refine_prompt',
      prompt: value,
      useArchitectData: false,
    }),
  });

  const data = (await res.json()) as { result?: string; error?: string };
  if (!res.ok) throw new Error(data.error || 'Prompt refiner is unavailable');

  const improved = String(data.result || '')
    // The model occasionally opens with quotes or a label despite the
    // system prompt — strip those defensively so the user always gets
    // clean prompt text.
    .replace(/^\s*(?:"|'|`|Rewritten:|Improved prompt:|Refined:|Here(?:'s| is)[^\n:]*:)\s*/i, '')
    .replace(/\s*(?:"|'|`)\s*$/g, '')
    .trim();

  if (!improved) throw new Error('The refiner returned an empty result');
  return improved;
}
